import { useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import firebase from 'firebase/compat/app';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import useProducts from '../../hooks/useProducts';
import { useAppSelector } from '../../hooks';
import { Unit } from '../../components/SelectUnit';

interface GroupForm {
  title: string;
  unit: Unit | '';
  products: { productId: string; storageAlert: number }[];
}

export default function AddGroup() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saving = useRef(false);
  const { uid, email, name } = useAppSelector((state) => state.user.details);
  const productsLoading = useAppSelector((state) => state.products.loading);
  const { products, dispatchFetchProducts } = useProducts();
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GroupForm>({
    defaultValues: { title: '', unit: '', products: [] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'products' });

  const submit = handleSubmit(async (values) => {
    if (saving.current) return;
    saving.current = true;
    setIsSaving(true);
    setError('');
    try {
      const storage = Object.fromEntries(
        values.products.map((product) => [
          product.productId,
          {
            storage: 0,
            storageAlert: product.storageAlert,
            unit: values.unit,
          },
        ])
      );
      await firebase
        .firestore()
        .collection('groups')
        .add({
          title: values.title.trim(),
          members: { [uid]: { uid, email, name: name || '' } },
          count: 0,
          ...(values.products.length > 0 ? { storage } : {}),
        });
      reset();
      setOpen(false);
      setCreated(true);
    } catch (error) {
      setError(t('Gruppen kunne ikke oprettes. Dine oplysninger er bevaret. Prøv igen.'));
    } finally {
      saving.current = false;
      setIsSaving(false);
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (saving.current) return;
        setOpen(nextOpen);
        if (nextOpen) {
          setCreated(false);
          dispatchFetchProducts();
        }
      }}
    >
      <span role="status" className="sr-only">
        {created ? t('Gruppen er oprettet') : ''}
      </span>
      <DialogTrigger asChild>
        <Button>
          <Plus aria-hidden="true" />
          {t('group.addgroup')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto rounded-xl sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('group.addForm.header')}</DialogTitle>
          <DialogDescription>
            {t('Saml afdelinger i en gruppe, og vælg eventuelt produkter til hovedlageret.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          <fieldset disabled={isSaving} className="min-w-0 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="group-title">{t('group.addForm.nameLabel')}</Label>
              <Input
                id="group-title"
                placeholder={t('group.addForm.namePlaceholder')}
                aria-invalid={!!errors.title}
                aria-describedby={errors.title ? 'group-title-error' : undefined}
                {...register('title', {
                  validate: (value) => !!value.trim() || t('Venligst udfyld navn!'),
                })}
              />
              {errors.title && (
                <p id="group-title-error" role="alert" className="text-sm text-destructive">
                  {errors.title.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-unit">{t('group.addForm.unitLabel')}</Label>
              <Controller
                control={control}
                name="unit"
                rules={{ required: t('Venligst vælg enhed!') }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange} disabled={isSaving}>
                    <SelectTrigger
                      id="group-unit"
                      ref={field.ref}
                      onBlur={field.onBlur}
                      aria-invalid={!!errors.unit}
                      aria-describedby={errors.unit ? 'group-unit-error' : undefined}
                    >
                      <SelectValue placeholder={t('group.addForm.unitPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={Unit.LITER}>{t('units.liter')}</SelectItem>
                      <SelectItem value={Unit.MILILITER}>{t('units.milliliter')}</SelectItem>
                      <SelectItem value={Unit.PIECE}>{t('units.stk')}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.unit && (
                <p id="group-unit-error" role="alert" className="text-sm text-destructive">
                  {errors.unit.message}
                </p>
              )}
            </div>
            <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
              <div>
                <h3 className="m-0 text-sm font-medium">
                  {t('Produkter på hovedlageret')}{' '}
                  <span className="font-normal text-muted-foreground">({t('valgfrit')})</span>
                </h3>
                <p className="mb-0 mt-1 text-sm text-muted-foreground">
                  {t('Beholdningen starter på 0. Angiv, hvornår der skal vises en lageradvarsel.')}
                </p>
              </div>
              {fields.map((field, index) => (
                <div key={field.id} className="space-y-2 rounded-md border bg-background p-3">
                  <div className="flex items-end gap-2">
                    <div className="min-w-0 flex-1 space-y-2">
                      <Label htmlFor={`group-product-${field.id}`}>
                        {t('Produkt')} {index + 1}
                      </Label>
                      <Controller
                        control={control}
                        name={`products.${index}.productId`}
                        rules={{
                          required: t('Vælg et produkt'),
                          validate: (value, values) =>
                            values.products.filter((product) => product.productId === value)
                              .length === 1 || t('Produktet er allerede tilføjet'),
                        }}
                        render={({ field: productField }) => (
                          <Select
                            value={productField.value}
                            onValueChange={productField.onChange}
                            disabled={isSaving}
                          >
                            <SelectTrigger
                              id={`group-product-${field.id}`}
                              ref={productField.ref}
                              onBlur={productField.onBlur}
                              aria-invalid={!!errors.products?.[index]?.productId}
                              aria-describedby={
                                errors.products?.[index]?.productId
                                  ? `group-product-error-${field.id}`
                                  : undefined
                              }
                            >
                              <SelectValue placeholder={t('Vælg et produkt')} />
                            </SelectTrigger>
                            <SelectContent>
                              {products.map((product) => (
                                <SelectItem key={product.id} value={product.id}>
                                  {product.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`${t('Fjern produkt')} ${index + 1}`}
                      onClick={() => remove(index)}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  </div>
                  {errors.products?.[index]?.productId && (
                    <p
                      id={`group-product-error-${field.id}`}
                      role="alert"
                      className="text-sm text-destructive"
                    >
                      {errors.products[index]?.productId?.message}
                    </p>
                  )}
                  <Label className="block" htmlFor={`group-threshold-${field.id}`}>
                    {t('Grænse for lav beholdning')}
                  </Label>
                  <Input
                    id={`group-threshold-${field.id}`}
                    type="number"
                    min={1}
                    step="any"
                    aria-invalid={!!errors.products?.[index]?.storageAlert}
                    aria-describedby={
                      errors.products?.[index]?.storageAlert
                        ? `group-threshold-error-${field.id}`
                        : undefined
                    }
                    {...register(`products.${index}.storageAlert`, {
                      valueAsNumber: true,
                      validate: (value) =>
                        (Number.isFinite(value) && value >= 1) || t('Angiv en grænse på mindst 1'),
                    })}
                  />
                  {errors.products?.[index]?.storageAlert && (
                    <p
                      id={`group-threshold-error-${field.id}`}
                      role="alert"
                      className="text-sm text-destructive"
                    >
                      {errors.products[index]?.storageAlert?.message}
                    </p>
                  )}
                </div>
              ))}
              {productsLoading ? (
                <p role="status" className="text-sm text-muted-foreground">
                  {t('Indlæser produkter…')}
                </p>
              ) : products.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  <p>
                    {t(
                      'Ingen produkter tilgængelige. Du kan stadig oprette gruppen uden produkter.'
                    )}
                  </p>
                  <Button type="button" variant="outline" size="sm" onClick={dispatchFetchProducts}>
                    {t('Hent produkter igen')}
                  </Button>
                </div>
              ) : null}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={productsLoading || products.length === 0}
                onClick={() => append({ productId: '', storageAlert: 1 })}
              >
                <Plus aria-hidden="true" />
                {t('Tilføj produkt')}
              </Button>
            </div>
          </fieldset>
          {error && (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {error}
            </p>
          )}
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isSaving}
              onClick={() => setOpen(false)}
            >
              {t('Annuller')}
            </Button>
            <Button type="submit" loading={isSaving} disabled={isSaving}>
              {t(isSaving ? 'Opretter…' : 'Opret gruppe')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
